/* eslint-disable indent */
import _ from 'lodash';
import sirenParse from 'siren-parser';
import { DateTime } from 'luxon';
import { entityStore } from '~/utility/BinSentry-ui-utility';
import { useOrdersDialogStateSelector } from '../context/orders-dialog-context';

export const dayKeyFormat = 'yyyy-MM-dd';
export const isoDateToDayKey = (isoDate) => DateTime.fromISO(isoDate).toFormat(dayKeyFormat);

export const getActionFieldNames = (action, requiredClasses = []) => {
  const { fields } = action || {};
  return fields
    .filter(field =>
      requiredClasses.every(requiredClass => field.class?.includes(requiredClass)),
    // eslint-disable-next-line quotes
    ).map(field => field.name);
};

export const getActionFields = (action, requiredClasses = []) => {
  const { fields } = action || {};

  return _(fields)
    .filter(field =>
      requiredClasses.every(requiredClass => field.class?.includes(requiredClass)),
    )
    .keyBy(field => field.name)
    .mapValues(field => {
      if (field.type === 'radio') {
        return field.value.find(radioField => radioField.checked)?.value === 'true';
      }
      return field.value;
    })
    .value();
};

export const fetchAndParseEntity = async (uri, bypassCache = false) => {
  if (typeof uri !== 'string') {
    throw new Error('fetchAndParseEntity | No uri given');
  }
  const { entity } = await entityStore.fetchWait(uri, bypassCache);
  return sirenParse(entity);
};

export const shouldIncludeOrderInAdditionalVolume = (orderEntity) => !orderEntity || ['scheduled', 'ready'].includes(orderEntity?.properties.state);

export const getOrdersAllocations = (orderEntity) => {
  const allocations = orderEntity.getSubEntitiesByRel('https://api.binsentry.com/rel/order-bin-allocation');
  const totalVolume = orderEntity.properties.quantity;

  return allocations.map(allocationEntity => {
    const { volume } = allocationEntity.properties;
    const binId = allocationEntity.getLinkByRel('https://api.binsentry.com/rel/bin').href;

    return {
      volume,
      totalVolume,
      binId,
      feedId: orderEntity.getSubEntityByRel('https://api.binsentry.com/rel/feed')?.href,
      ..._.pick(orderEntity.properties, ['deliveryDate', 'medicated', 'type', 'state']),
      orderId: orderEntity.getLinkByRel('self').href,
      orderEntity,
      allocationEntity,
    };
  });
};

export function useOrderVolume({ orderId }) {
  const [
    selectedBarnDaysOrders,
    selectedBarnsBins,
    selectedOrderEntity,
  ] = useOrdersDialogStateSelector([
    'selectedBarnDaysOrders',
    'selectedBarnsBins',
    'selectedOrderEntity',
  ]);

  const [unsavedAllocationsByBinId] = useOrdersDialogStateSelector([
    (state) => _(state.unsavedChanges || {})
      .pickBy((_value, key) => key.startsWith('binAllocations'))
      .mapKeys((_value, key) => key.replace(/(binAllocations\[)|(\]$)/g, '')) // remove the binAllocations[] bit to be easier to work with
      .value(),
  ]);

  const modifiedSelectedBarnDayOrders = [
    ...selectedOrderEntity ? [selectedOrderEntity] : [],
    ...selectedBarnDaysOrders.filter(order => order.getLinkByRel('self').href !== selectedOrderEntity?.getLinkByRel('self').href),
  ];
  const selectedBarnDayOrdersContainsOrder = modifiedSelectedBarnDayOrders.some(order => order.getLinkByRel('self').href === orderId);

  let orderVolume = _.sumBy(modifiedSelectedBarnDayOrders, (order) => {
    if (order.getLinkByRel('self').href !== orderId) {
      return 0;
    }

    let allocationsVolume = 0;
    selectedBarnsBins.forEach(entity => {
      const binId = entity.getLinkByRel('self').href;
      if (Number.isFinite(unsavedAllocationsByBinId[binId])) {
        // The user has entered a value, use that over existing volume
        allocationsVolume += unsavedAllocationsByBinId[binId];
      } else {
        const orderAllocations = order.getSubEntitiesByRel('https://api.binsentry.com/rel/order-bin-allocation');
        const orderBinAllocation = orderAllocations.find(allocation => allocation.getLinkByRel('https://api.binsentry.com/rel/bin').href === binId);
        allocationsVolume += orderBinAllocation?.properties.volume || 0;
      }
    });
    return allocationsVolume;
  });

  if (!selectedBarnDayOrdersContainsOrder) {
    orderVolume += _.sum(Object.values(unsavedAllocationsByBinId)) || 0;
  }

  return orderVolume;
}

export const windowConfirmUnsavedChanges = () => window.confirm('You have unsaved changes that will be lost. Continue?');

export const windowConfirmDeleteOrder = () => window.confirm('Once an order is deleted, it cannot be undone. Continue?');

export const getSelectNewOrderEntityState = ({ state, newOrderEntity }) => {
  const selectedBarnId = newOrderEntity?.getLinkByRel('https://api.binsentry.com/rel/barn').href || state.selectedBarnId;
  const selectedBarn = state.barns[selectedBarnId];
  const selectedBarnEntity = selectedBarn.barnEntity;
  const selectedBarnsBins = selectedBarn.binEntities || [];
  const selectedBarnOrganizationBinEntity = selectedBarn.organizationBinEntity;
  const selectedBarnOrdersEntity = selectedBarn.ordersEntity;

  const barnsBinsIds = selectedBarnsBins.map(bin => bin.getLinkByRel('self').href);

  const selectedOrderId = newOrderEntity?.getLinkByRel('self').href;
  const selectedBarnOrganizationBinLevelsEntity = state.barnBinLevelEntities.find(levelEntity => levelEntity.getSubEntityByRel('https://api.binsentry.com/rel/bin').href === selectedBarnOrganizationBinEntity.getLinkByRel('self').href);
  const selectedBarnDaysAllocations = state.daysAllocations.filter(allocation => barnsBinsIds.includes(allocation.binId));
  const selectedAllocations = selectedBarnDaysAllocations.filter(allocation => allocation.orderId === selectedOrderId);

  const dataToMerge = {
    selectedOrderEntity: newOrderEntity,
    selectedOrderId,
    specifyingNewOrder: !newOrderEntity,
    selectedBarnEntity,
    selectedBarnOrganizationBinEntity,
    selectedBarnOrganizationBinLevelsEntity,
    selectedBarnDaysOrders: state.daysOrders.filter(order => order.getLinkByRel('https://api.binsentry.com/rel/barn').href === selectedBarnId),
    selectedBarnDaysAllocations,
    selectedBarnOrdersEntity,
    selectedAllocations,
    selectedBarnId,
    selectedBarnsBins,
    unsavedChanges: {},
    newDataEntered: false,
    hasUnsavedChanges: false,
    createOrEditSirenAction: undefined,
  };

  const canCreateOrder = state.selectedBarnOrdersEntity?.getAction('create-order');
  const dateIsPresentOrFuture = DateTime.fromISO(state.isoDate).startOf('day') >= DateTime.fromISO(state.usersCurrentLocalIsoDate).startOf('day');
  const canRequestOrder = state.selectedBarnOrdersEntity?.getAction('request-order');
  dataToMerge.approveSirenAction = newOrderEntity?.getAction('approve-order');

  if (dataToMerge.specifyingNewOrder && dateIsPresentOrFuture) {
    dataToMerge.createOrEditSirenAction = canCreateOrder || canRequestOrder;
  } else {
    dataToMerge.createOrEditSirenAction = newOrderEntity?.getAction('schedule-order') || newOrderEntity?.getAction('update-order');
  }

  return { ...dataToMerge };
};
